import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { OrganizationsContextProvier } from "go-app/utils/OrganizationsContext";
import { BrowserTabProvider } from "go-core/components/BrowserTab/BrowserTab";
import EmptyList from "go-core/components/EmptyList";
import BlankLayout from "go-core/components/Layout/BlankLayout";
import { LoadingContainer } from "go-core/components/Loading";
import Navigation from "../../components/Navigation";
import { ConfigApi } from "../../services/Api/types";
import { ConfigContext, ConfigService } from "../../services/Config/context";
import { IntercomApp } from "../../services/Intercom/intercomApp";
import { api } from "../../services/Security/api";
import { useAuth } from "../../services/Security/auth";
import { isLicenseExpired } from "../../services/license";
import { selectOrganization } from "../../services/organizations/selectors";
import { selectIsAuthenticated } from "../../services/session/selectors";
import { selectUser } from "../../services/users/selectors";
import SecurityAcceptTerms from "../security/UserAcceptTerms/UserAcceptTerms";
import UnverifiedEmailNotification from "./components/UnverifiedEmailNotification/UnverifiedEmailNotification";
import AdminPage from "./pages/Admin";
import EnterApplicationPage from "./pages/EnterApplication";
import OrganizationPage from "./pages/Organization";
import PartnerPage from "./pages/Partner";
import SupportPage from "./pages/Support";
import UserPage from "./pages/User";

const AppPage = ({ match }: RouteComponentProps) => {
	const [loading, setLoading] = useState(true);
	const [config, setConfig] = useState<ConfigApi>();
	const [error, setError] = useState<string | undefined>(undefined);
	const auth = useAuth();
	const user = useSelector(selectUser);
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const { t } = useTranslation();
	const { boot, update } = useIntercom();
	const isUserInOrganization = !!user?.organizations?.find((org) => org.id === organization.id);
	const notAcceptedTerms = !organization.accept_terms || !organization.accept_privacy || !organization.accept_rules;
	const isAcceptTermsRoute = location.pathname === `/${organization.id}/accept_terms`;

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		initIntercom();
	}, [user.intercom_id, user.intercom_hash_web, organization.intercom_company_id]);

	const checkLogin = async () => {
		if (isAuthenticated) return;
		try {
			await auth.checkLogin();
		} catch (err) {
			setError(t("lib:constraints.unknown_error"));
		}
	};

	const fetchConfig = async () => {
		try {
			const config = await api.getConfig();
			setConfig(config.data.data);
		} catch (err) {
			setError(t("lib:constraints.unknown_error"));
		}
	};

	const initIntercom = () => {
		const cfg = IntercomApp.cfg(user, organization);
		if (!cfg) return;

		if (!IntercomApp.isInitialized()) {
			IntercomApp.initialize();
			boot(cfg);
		} else {
			update(cfg);
		}
	};

	const init = async () => {
		await Promise.all([fetchConfig(), checkLogin()]);
		setLoading(false);
	};

	if (loading) {
		return <LoadingContainer />;
	}

	if (error) {
		return (
			<EmptyList
				title={error}
				actions={[
					{
						name: t("lib:common.action.refresh"),
						click: () => {
							window.location.reload();
						},
					},
				]}
			/>
		);
	}

	if (!isAuthenticated) {
		const urlSearchParams = new URLSearchParams(location.search);
		const acceptation_code = urlSearchParams.get("acceptation_code");
		if (acceptation_code !== undefined && acceptation_code) {
			return (
				<Redirect
					to={{
						pathname: "/invitation",
						search: `?acceptation_code=${acceptation_code}`,
						state: { from: location },
					}}
				/>
			);
		}
		return (
			<Redirect
				to={{
					pathname: "/login",
					state: { from: location },
				}}
			/>
		);
	}

	const checkIfRenderMarginWhenNotAcceptedTermsByOrganization = () => {
		if (isAcceptTermsRoute) return false;

		if (organization.id) {
			if (!isUserInOrganization && notAcceptedTerms && (organization?.admin || organization?.partner)) {
				return true;
			}
			if (notAcceptedTerms && (organization?.admin || organization?.partner)) {
				return !isAcceptTermsRoute;
			}
		}
		if (notAcceptedTerms && !organization.partner && !organization.admin) return false;
		return false;
	};

	const getMargin = (): string => {
		if (!user?.verified) {
			if (isLicenseExpired(organization.license_expires_at, organization.license_type, organization.timezone)) {
				if (checkIfRenderMarginWhenNotAcceptedTermsByOrganization()) return "138px";
				return "92px";
			}
			if (checkIfRenderMarginWhenNotAcceptedTermsByOrganization()) return "92px";
			return "46px";
		}
		if (isLicenseExpired(organization.license_expires_at, organization.license_type, organization.timezone)) {
			if (checkIfRenderMarginWhenNotAcceptedTermsByOrganization()) return "92px";
			return "46px";
		}
		if (checkIfRenderMarginWhenNotAcceptedTermsByOrganization()) return "46px";
		return "";
	};

	if (config === undefined) {
		return (
			<EmptyList
				title={"Wystąpił problem"}
				actions={[
					{
						name: t("lib:common.action.refresh"),
						click: () => {
							window.location.reload();
						},
					},
				]}
			/>
		);
	}

	return (
		<OrganizationsContextProvier>
			<BlankLayout>
				<BrowserTabProvider>
					<ConfigContext.Provider value={new ConfigService(config)}>
						{!user?.accept_terms ? (
							<div className="page">
								<Navigation />
								<SecurityAcceptTerms />
							</div>
						) : (
							<>
								{!user?.verified && <UnverifiedEmailNotification />}
								<div className="page" style={getMargin() !== "" ? { marginTop: getMargin() } : {}}>
									<Navigation />
									<Switch>
										<Route
											path={[
												`${match.url}partnership`,
												`${match.url}support`,
												`${match.url}enter_organization`,
												`${match.url}enter_application`,
												`${match.url}dashboard`,
												`${match.url}logs`,
												`${match.url}admin`,
												`${match.url}profile`,
											]}
											component={OutsideRoutes}
										/>
										<Route path={`${match.url}:organizationId`} component={OrganizationPage} />
										<Route path={[`/`]} component={OutsideRoutes} />
									</Switch>
								</div>
							</>
						)}
					</ConfigContext.Provider>
				</BrowserTabProvider>
			</BlankLayout>
		</OrganizationsContextProvier>
	);
};
const OutsideRoutes = () => {
	const auth = useAuth();

	useEffect(() => {
		auth.logoutOrganization();
	}, []);

	return (
		<Switch>
			<Route path={"/admin"} component={AdminPage} />
			<Route path={"/partnership"} component={PartnerPage} />
			<Route path={"/support"} component={SupportPage} />
			<Route path={["/enter_organization", "/enter_application"]} component={EnterApplicationPage} />
			<Route path={["/profile", "/dashboard", "/logs"]} component={UserPage} />
			<Route path={["/"]} component={UserPage} />
		</Switch>
	);
};

export default AppPage;
